<template>
  <div class="payment-failed-container">
    <div class="failed-content">
      <!-- 失败图标 -->
      <div class="failed-icon">
        <i class="el-icon-error"></i>
      </div>
      
      <!-- 失败提示 -->
      <h2 class="failed-title">支付失败</h2>
      <p class="failed-message">{{ errorMessage || '支付过程中出现问题，请重试' }}</p>
      
      <!-- 订单信息 -->
      <div class="order-info" v-if="orderNo">
        <div class="info-item">
          <span class="info-label">订单编号：</span>
          <span class="info-value">{{ orderNo }}</span>
        </div>
      </div>
      
      <!-- 失败原因说明 -->
      <div class="failure-reasons" v-if="failureReasons.length > 0">
        <h3 class="reasons-title">可能的原因：</h3>
        <ul class="reasons-list">
          <li v-for="(reason, index) in failureReasons" :key="index">{{ reason }}</li>
        </ul>
      </div>
      
      <!-- 操作按钮 -->
      <div class="action-buttons">
        <el-button type="primary" @click="retryPayment">重新支付</el-button>
        <el-button @click="goToOrderConfirm">返回订单</el-button>
        <el-button @click="goToProductList">返回商城</el-button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 支付失败页面组件
 * 业务背景：支付失败时需要明确告知用户失败原因，并提供重试或返回的选项
 * 设计决策：展示清晰的失败提示和可能原因，提供多种操作选项（重试、返回订单、返回商城），帮助用户快速处理问题
 */
export default {
  name: "PaymentFailed",
  data() {
    return {
      orderNo: '', // 订单编号
      errorMessage: '', // 错误信息
      failureReasons: [
        '网络连接异常，请检查网络后重试',
        '账户余额不足，请充值后重试',
        '支付超时，请重新发起支付',
        '订单已过期，请重新下单'
      ] // 失败原因列表
    };
  },

  /**
   * 组件创建时从路由参数或 sessionStorage 获取订单信息
   * 业务背景：支付失败后需要展示订单信息，但路由参数可能在刷新后丢失
   * 设计决策：优先从路由参数获取，如果没有则从 sessionStorage 获取，同时解析错误信息
   */
  created() {
    // 从路由参数获取订单信息和错误信息
    const params = this.$route.params;
    if (params.orderNo) {
      this.orderNo = params.orderNo;
    }
    if (params.errorMessage) {
      this.errorMessage = params.errorMessage;
    }
    
    // 如果没有路由参数，尝试从 sessionStorage 获取
    if (!this.orderNo) {
      const orderData = sessionStorage.getItem('lastOrderInfo');
      if (orderData) {
        try {
          const orderInfo = JSON.parse(orderData);
          this.orderNo = orderInfo.orderNo || '';
        } catch (e) {
          console.error('解析订单信息失败:', e);
        }
      }
    }
    
    // 从查询参数获取错误信息
    const query = this.$route.query;
    if (query.error) {
      this.errorMessage = decodeURIComponent(query.error);
    }
  },

  methods: {
    /**
     * 重新支付
     * 业务背景：用户可能想重新尝试支付
     * 设计决策：跳转回订单确认页，保留订单信息，方便用户重新支付
     */
    retryPayment() {
      if (this.orderNo) {
        // 如果有订单编号，尝试跳转到订单确认页
        // 注意：这里需要根据实际业务逻辑调整
        this.$router.push({
          name: 'OrderConfirm'
        });
      } else {
        // 如果没有订单编号，返回商品列表
        this.goToProductList();
      }
    },

    /**
     * 返回订单确认页面
     * 业务背景：用户可能想查看订单详情或修改订单信息
     * 设计决策：提供返回订单确认页的快捷入口
     */
    goToOrderConfirm() {
      this.$router.push({
        name: 'OrderConfirm'
      });
    },

    /**
     * 跳转到商品列表页面
     * 业务背景：用户可能想放弃当前订单，重新选择商品
     * 设计决策：提供返回商城的快捷入口
     */
    goToProductList() {
      this.$router.push({
        name: 'ProductList'
      });
    }
  }
};
</script>

<style scoped>
.payment-failed-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 20px;
}

.failed-content {
  background: #fff;
  border-radius: 12px;
  padding: 60px 40px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 100%;
}

.failed-icon {
  margin-bottom: 30px;
}

.failed-icon i {
  font-size: 80px;
  color: #f56c6c;
}

.failed-title {
  font-size: 28px;
  color: #333;
  margin-bottom: 15px;
  font-weight: 600;
}

.failed-message {
  font-size: 16px;
  color: #f56c6c;
  margin-bottom: 40px;
  line-height: 1.6;
  font-weight: 500;
}

.order-info {
  background: #f5f7fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  text-align: left;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
}

.info-label {
  color: #666;
}

.info-value {
  color: #333;
  font-weight: 500;
}

.failure-reasons {
  background: #fef0f0;
  border: 1px solid #fde2e2;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 40px;
  text-align: left;
}

.reasons-title {
  font-size: 16px;
  color: #333;
  margin-bottom: 15px;
  font-weight: 600;
}

.reasons-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.reasons-list li {
  font-size: 14px;
  color: #666;
  line-height: 1.8;
  padding-left: 20px;
  position: relative;
}

.reasons-list li:before {
  content: '•';
  position: absolute;
  left: 0;
  color: #f56c6c;
  font-weight: bold;
}

.action-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
}

.action-buttons .el-button {
  padding: 12px 30px;
  font-size: 16px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .failed-content {
    padding: 40px 20px;
  }

  .failed-icon i {
    font-size: 60px;
  }

  .failed-title {
    font-size: 24px;
  }

  .failed-message {
    font-size: 14px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .action-buttons .el-button {
    width: 100%;
  }
}
</style>

